import { type PropsWithChildren, useEffect, useRef, useState } from "react";
import type { SessionResponseDto } from "@madamuh/domain";
import { useLocation } from "react-router-dom";
import {
  HamburgerIcon,
} from "../icons/AppIcons";
import { FamilyBottomNavigation, type FamilyBottomNavigationTab } from "./FamilyBottomNavigation";
import { FamilySideDrawer } from "./FamilySideDrawer";
import {
  useFamilyNavigationCounts,
  type FamilyNavigationSeedCounts
} from "./useFamilyNavigationCounts";
import { useProtectedRoleDrawer } from "../protected/useProtectedRoleDrawer";
import { CircularAvatarEditorDialog } from "../profile/CircularAvatarEditorDialog";
import { useViewerProfilePhoto } from "../profile/useViewerProfilePhoto";
import "./family-shell.css";

type FamilyAppShellProps = PropsWithChildren<{
  tenantSlug: string;
  tenantName: string | null | undefined;
  session: SessionResponseDto | null;
  activeTab: FamilyBottomNavigationTab;
  navigationCountsSeed?: FamilyNavigationSeedCounts;
  navigationCountsEnabled?: boolean;
}>;

export function FamilyAppShell({
  tenantSlug,
  tenantName,
  session,
  activeTab,
  navigationCountsSeed,
  navigationCountsEnabled = true,
  children
}: FamilyAppShellProps) {
  const location = useLocation();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
  const navigationCounts = useFamilyNavigationCounts(tenantSlug, navigationCountsSeed, navigationCountsEnabled);
  const { actionItems, errorMessage, viewerName, viewerRoleLabel } = useProtectedRoleDrawer({
    tenantSlug,
    session,
    currentArea: "family"
  });
  const viewerPhoto = useViewerProfilePhoto(tenantSlug, "family", Boolean(session));

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDrawerOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [drawerOpen]);

  return (
    <main className="family-app-shell">
      <div className="family-app-shell__viewport">
        <header className="family-app-shell__header">
          <div className="family-app-shell__brand">
            <h1 className="family-app-shell__brand-name">{tenantName || "MADAMUH LESSON"}</h1>
          </div>
          <button
            type="button"
            className="family-app-shell__menu-button"
            aria-label="メニューを開く"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <HamburgerIcon size={28} />
          </button>
        </header>

        <section className="family-app-shell__content family-app-shell__content--with-nav">{children}</section>
      </div>

      <FamilyBottomNavigation tenantSlug={tenantSlug} activeTab={activeTab} counts={navigationCounts} />
      <FamilySideDrawer
        open={drawerOpen}
        tenantSlug={tenantSlug}
        actionItems={actionItems}
        closeButtonRef={closeButtonRef}
        errorMessage={errorMessage}
        viewerName={viewerName}
        viewerRoleLabel={viewerRoleLabel}
        viewerPhotoUrl={viewerPhoto.photo?.downloadUrl ?? null}
        onEditViewerPhoto={() => { setDrawerOpen(false); setPhotoEditorOpen(true); }}
        onClose={() => setDrawerOpen(false)}
      />
      <CircularAvatarEditorDialog open={photoEditorOpen} onCancel={() => setPhotoEditorOpen(false)} onSet={async (blob) => { await viewerPhoto.set(blob); }} />
    </main>
  );
}
